import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Users, 
  CreditCard, 
  Calendar, 
  AlertCircle,
  DollarSign
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardStats, getUpcomingEvents, getAllDebts } from '../services/api';
import { DashboardStats, Event, Debt } from '../types';

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const WelcomeSection = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px;
  border-radius: 15px;
  margin-bottom: 30px;
  text-align: center;
`;

const WelcomeTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 10px;
  font-weight: 700;
`;

const WelcomeSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: white;
  padding: 25px;
  border-radius: 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-right: 4px solid ${props => props.color || '#667eea'};
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 14px;
  font-weight: 500;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DebtorItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 10px;
  border-right: 4px solid #dc3545;
`;

const DebtorName = styled.div`
  font-weight: 600;
  color: #333;
`;

const DebtorAmount = styled.div`
  font-weight: 700;
  color: #dc3545;
  font-size: 1.1rem;
`;

const EventItem = styled.div`
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 10px;
  border-right: 4px solid #28a745;
`;

const EventTitle = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
`;

const EventDate = styled.div`
  color: #666;
  font-size: 14px;
`;

const EmptyState = styled.div`
  text-align: center;
  color: #666;
  padding: 40px 20px;
  font-style: italic;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: #666;
`;

const Dashboard: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [debts, setDebts] = useState<Debt[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        if (isAdmin) {
          const [statsData, eventsData, debtsData] = await Promise.all([
            getDashboardStats(),
            getUpcomingEvents(5),
            getAllDebts()
          ]);
          
          setStats(statsData);
          setEvents(eventsData);
          setDebts(debtsData);
        } else {
          // For members, load their personal data
          const [eventsData, debtsData] = await Promise.all([
            getUpcomingEvents(5),
            getAllDebts() // This should be filtered by member ID in real implementation
          ]);
          
          setEvents(eventsData);
          setDebts(debtsData);
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [isAdmin]);

  if (loading) {
    return (
      <LoadingSpinner>
        טוען נתונים...
      </LoadingSpinner>
    );
  }

  const memberDebts = debts.filter(debt => debt.memberId === user?.id);
  const totalMemberDebt = memberDebts.reduce((sum, debt) => sum + (debt.remainingAmount || 0), 0);

  return (
    <DashboardContainer>
      <WelcomeSection>
        <WelcomeTitle>
          שלום {user?.name} {user?.lastName}
        </WelcomeTitle>
        <WelcomeSubtitle>
          {isAdmin ? 'ברוכים הבאים לפאנל הניהול' : 'ברוכים הבאים לחשבון האישי שלך'}
        </WelcomeSubtitle>
      </WelcomeSection>

      {isAdmin ? (
        <>
          <StatsGrid>
            <StatCard color="#667eea">
              <StatHeader>
                <StatIcon color="#667eea">
                  <Users size={24} />
                </StatIcon>
              </StatHeader>
              <StatValue>{stats?.totalMembers || 0}</StatValue>
              <StatLabel>סה"כ מתפללים</StatLabel>
            </StatCard>

            <StatCard color="#28a745">
              <StatHeader>
                <StatIcon color="#28a745">
                  <DollarSign size={24} />
                </StatIcon>
              </StatHeader>
              <StatValue>₪{(stats?.totalPaid || 0).toLocaleString()}</StatValue>
              <StatLabel>סה"כ נגבה</StatLabel>
            </StatCard>

            <StatCard color="#dc3545">
              <StatHeader>
                <StatIcon color="#dc3545">
                  <AlertCircle size={24} />
                </StatIcon>
              </StatHeader>
              <StatValue>₪{(stats?.totalUnpaid || 0).toLocaleString()}</StatValue>
              <StatLabel>סה"כ חובות</StatLabel>
            </StatCard>

            <StatCard color="#ffc107">
              <StatHeader>
                <StatIcon color="#ffc107">
                  <Calendar size={24} />
                </StatIcon>
              </StatHeader>
              <StatValue>{stats?.upcomingEvents || 0}</StatValue>
              <StatLabel>אירועים קרובים</StatLabel>
            </StatCard>
          </StatsGrid>

          <ContentGrid>
            <Section>
              <SectionTitle>
                <AlertCircle size={20} />
                חייבים מובילים
              </SectionTitle>
              {stats?.topDebtors && stats.topDebtors.length > 0 ? (
                stats.topDebtors.slice(0, 5).map((debtor, index) => (
                  <DebtorItem key={debtor.memberId}>
                    <DebtorName>{debtor.memberName}</DebtorName>
                    <DebtorAmount>₪{(debtor.totalDebt || 0).toLocaleString()}</DebtorAmount>
                  </DebtorItem>
                ))
              ) : (
                <EmptyState>אין חייבים כרגע</EmptyState>
              )}
            </Section>

            <Section>
              <SectionTitle>
                <Calendar size={20} />
                אירועים קרובים
              </SectionTitle>
              {events.length > 0 ? (
                events.map((event) => (
                  <EventItem key={event.id}>
                    <EventTitle>{event.title}</EventTitle>
                    <EventDate>
                      {(() => {
                        try {
                          let date;
                          if (event.eventDate && typeof event.eventDate === 'object' && 'toDate' in event.eventDate) {
                            date = event.eventDate.toDate();
                          } else if (event.eventDate instanceof Date) {
                            date = event.eventDate;
                          } else {
                            date = new Date(event.eventDate);
                          }
                          return date.toLocaleDateString('he-IL');
                        } catch (error) {
                          console.warn('Error displaying event date:', error);
                          return 'תאריך לא תקין';
                        }
                      })()}
                    </EventDate>
                  </EventItem>
                ))
              ) : (
                <EmptyState>אין אירועים קרובים</EmptyState>
              )}
            </Section>
          </ContentGrid>
        </>
      ) : (
        <ContentGrid>
          <Section>
            <SectionTitle>
              <CreditCard size={20} />
              החובות שלי
            </SectionTitle>
            {memberDebts.length > 0 ? (
              <>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <StatValue style={{ color: totalMemberDebt > 0 ? '#dc3545' : '#28a745' }}>
                    ₪{totalMemberDebt.toLocaleString()}
                  </StatValue>
                  <StatLabel>סה"כ חוב נוכחי</StatLabel>
                </div>
                {memberDebts.map((debt) => (
                  <DebtorItem key={debt.id}>
                    <DebtorName>{debt.description || 'חוב כללי'}</DebtorName>
                    <DebtorAmount>₪{(debt.remainingAmount || 0).toLocaleString()}</DebtorAmount>
                  </DebtorItem>
                ))}
              </>
            ) : (
              <EmptyState>אין לך חובות כרגע</EmptyState>
            )}
          </Section>

          <Section>
            <SectionTitle>
              <Calendar size={20} />
              אירועים קרובים
            </SectionTitle>
            {events.length > 0 ? (
              events.map((event) => (
                <EventItem key={event.id}>
                  <EventTitle>{event.title}</EventTitle>
                  <EventDate>
                    {(() => {
                      try {
                        let date;
                        if (event.eventDate && typeof event.eventDate === 'object' && 'toDate' in event.eventDate) {
                          date = event.eventDate.toDate();
                        } else if (event.eventDate instanceof Date) {
                          date = event.eventDate;
                        } else {
                          date = new Date(event.eventDate);
                        }
                        return date.toLocaleDateString('he-IL');
                      } catch (error) {
                        console.warn('Error displaying event date:', error);
                        return 'תאריך לא תקין';
                      }
                    })()}
                  </EventDate>
                </EventItem>
              ))
            ) : (
              <EmptyState>אין אירועים קרובים</EmptyState>
            )}
          </Section>
        </ContentGrid>
      )}
    </DashboardContainer>
  );
};

export default Dashboard;
